import PageHeader from "@/components/common/PageHeader";
import {
  analyticsService,
  type ChurnData,
  type FeatureAdoption,
  type MRRTrendPoint,
  type RevenueByPlan,
  type TenantHealth,
} from "@/services/analytics.service";
import type { AnalyticsOverview, RevenueReport } from "@/types";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  AlertTriangle,
  BarChart3,
  DollarSign,
  PieChart,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";
import type { ReactNode } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const COLORS = [
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
  "#ec4899",
  "#14b8a6",
];

export default function AnalyticsDashboard() {
  const { data: overview, isLoading: loadingOverview } =
    useQuery<AnalyticsOverview>({
      queryKey: ["analytics", "overview"],
      queryFn: analyticsService.getOverview,
    });

  const { data: mrrTrends, isLoading: loadingMRR } = useQuery<MRRTrendPoint[]>({
    queryKey: ["analytics", "mrr-trends"],
    queryFn: () => analyticsService.getMRRTrends(12),
  });

  const { data: churnData, isLoading: loadingChurn } = useQuery<ChurnData[]>({
    queryKey: ["analytics", "churn"],
    queryFn: () => analyticsService.getChurnData(12),
  });

  const { data: featureAdoption, isLoading: loadingAdoption } = useQuery<
    FeatureAdoption[]
  >({
    queryKey: ["analytics", "feature-adoption"],
    queryFn: analyticsService.getFeatureAdoption,
  });

  const { data: tenantHealth, isLoading: loadingHealth } = useQuery<
    TenantHealth[]
  >({
    queryKey: ["analytics", "tenant-health"],
    queryFn: () =>
      analyticsService.getTenantHealth({
        sortBy: "healthScore",
        order: "asc",
        limit: 20,
      }),
  });

  const { data: revenueByPlan, isLoading: loadingRevenue } = useQuery<
    RevenueByPlan[]
  >({
    queryKey: ["analytics", "revenue-by-plan"],
    queryFn: analyticsService.getRevenueByPlan,
  });

  const { data: revenueReport, isLoading: loadingRevenueReport } = useQuery<
    RevenueReport
  >({
    queryKey: ["analytics", "revenue-report"],
    queryFn: () => analyticsService.getRevenueReport(12),
  });

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Platform Analytics"
        subtitle="MRR trends, churn analysis, feature adoption, and tenant health"
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Monthly Recurring Revenue"
          value={
            loadingOverview
              ? "..."
              : formatCurrency(overview?.monthlyRecurringRevenue ?? 0)
          }
          icon={<DollarSign className="h-5 w-5 text-green-600" />}
          bgColor="bg-green-50"
        />
        <KPICard
          title="Active Tenants"
          value={loadingOverview ? "..." : `${overview?.activeTenants ?? 0}`}
          subtitle={`of ${overview?.totalTenants ?? 0} total`}
          icon={<Users className="h-5 w-5 text-blue-600" />}
          bgColor="bg-blue-50"
        />
        <KPICard
          title="Revenue (30 days)"
          value={
            loadingOverview
              ? "..."
              : formatCurrency(overview?.revenueLast30Days ?? 0)
          }
          icon={<TrendingUp className="h-5 w-5 text-purple-600" />}
          bgColor="bg-purple-50"
        />
        <KPICard
          title="Open Tickets"
          value={loadingOverview ? "..." : `${overview?.openTickets ?? 0}`}
          subtitle={`${overview?.trialsThisWeek ?? 0} new trials this week`}
          icon={<Activity className="h-5 w-5 text-orange-600" />}
          bgColor="bg-orange-50"
        />
      </div>

      {/* MRR Trends Chart */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-800">MRR Trends</h3>
          <BarChart3 className="h-5 w-5 text-slate-400" />
        </div>
        {loadingMRR ? (
          <div className="h-72 flex items-center justify-center text-slate-400">
            Loading...
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={mrrTrends ?? []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" fontSize={12} />
              <YAxis
                fontSize={12}
                tickFormatter={(v: number) => `₹${(v / 1000).toFixed(0)}K`}
              />
              <Tooltip
                formatter={(value: number | undefined) =>
                  formatCurrency(value ?? 0)
                }
                contentStyle={{
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="mrr"
                stroke="#3b82f6"
                strokeWidth={2}
                name="Total MRR"
                dot={{ r: 3 }}
              />
              <Line
                type="monotone"
                dataKey="newMrr"
                stroke="#10b981"
                strokeWidth={2}
                name="New MRR"
                dot={{ r: 3 }}
              />
              <Line
                type="monotone"
                dataKey="churnedMrr"
                stroke="#ef4444"
                strokeWidth={2}
                name="Churned MRR"
                dot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Churn Rate Chart */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800">
              Churn Analysis
            </h3>
            <TrendingDown className="h-5 w-5 text-red-400" />
          </div>
          {loadingChurn ? (
            <div className="h-56 flex items-center justify-center text-slate-400">
              Loading...
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={churnData ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" fontSize={12} />
                <YAxis fontSize={12} tickFormatter={(v: number) => `${v}%`} />
                <Tooltip
                  formatter={(
                    value: number | undefined,
                    name: string | undefined,
                  ) =>
                    name === "churnRate"
                      ? [`${(value ?? 0).toFixed(1)}%`, "Churn Rate"]
                      : [value ?? 0, name ?? "Value"]
                  }
                  contentStyle={{
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                  }}
                />
                <Bar
                  dataKey="churnRate"
                  fill="#ef4444"
                  radius={[4, 4, 0, 0]}
                  name="Churn Rate %"
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Revenue by Plan */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800">
              Revenue by Plan
            </h3>
            <PieChart className="h-5 w-5 text-slate-400" />
          </div>
          {loadingRevenue ? (
            <div className="h-56 flex items-center justify-center text-slate-400">
              Loading...
            </div>
          ) : (
            <div className="space-y-3">
              {(revenueByPlan ?? []).map((plan, index) => (
                <div key={plan.plan}>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700">
                      {plan.plan}
                    </span>
                    <span className="text-sm text-slate-500">
                      {formatCurrency(plan.revenue)} ({plan.tenantCount}{" "}
                      tenants)
                    </span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all"
                      style={{
                        width: `${plan.percentage}%`,
                        backgroundColor: COLORS[index % COLORS.length],
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_0.7fr] gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800">
              Cross-Tenant Revenue Report
            </h3>
            <DollarSign className="h-5 w-5 text-slate-400" />
          </div>
          {loadingRevenueReport ? (
            <div className="h-56 flex items-center justify-center text-slate-400">
              Loading...
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                <div className="rounded-xl bg-slate-50 p-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
                    Captured
                  </p>
                  <p className="mt-2 text-xl font-semibold text-slate-900">
                    {formatCurrency(revenueReport?.summary.capturedRevenue ?? 0)}
                  </p>
                </div>
                <div className="rounded-xl bg-amber-50 p-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-amber-500">
                    Refunded
                  </p>
                  <p className="mt-2 text-xl font-semibold text-amber-700">
                    {formatCurrency(revenueReport?.summary.refundedRevenue ?? 0)}
                  </p>
                </div>
                <div className="rounded-xl bg-green-50 p-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-green-500">
                    Net
                  </p>
                  <p className="mt-2 text-xl font-semibold text-green-700">
                    {formatCurrency(revenueReport?.summary.netRevenue ?? 0)}
                  </p>
                </div>
              </div>

              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={revenueReport?.timeline ?? []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" fontSize={12} />
                  <YAxis
                    fontSize={12}
                    tickFormatter={(v: number) => `₹${(v / 1000).toFixed(0)}K`}
                  />
                  <Tooltip
                    formatter={(value: number | undefined) =>
                      formatCurrency(value ?? 0)
                    }
                    contentStyle={{
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                    }}
                  />
                  <Legend />
                  <Bar
                    dataKey="capturedRevenue"
                    fill="#0f766e"
                    radius={[4, 4, 0, 0]}
                    name="Captured"
                  />
                  <Bar
                    dataKey="refundedRevenue"
                    fill="#f59e0b"
                    radius={[4, 4, 0, 0]}
                    name="Refunded"
                  />
                </BarChart>
              </ResponsiveContainer>
            </>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800">
              Top Revenue Tenants
            </h3>
            <Users className="h-5 w-5 text-slate-400" />
          </div>
          <div className="space-y-3">
            {(revenueReport?.topTenants ?? []).map((tenant) => (
              <div
                key={tenant.tenantId}
                className="rounded-xl border border-slate-100 px-4 py-3"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-medium text-slate-900">
                      {tenant.tenantName}
                    </p>
                    <p className="text-xs text-slate-500">
                      {tenant.paymentCount} payments
                    </p>
                  </div>
                  <p className="text-sm font-semibold text-slate-900">
                    {formatCurrency(tenant.revenue)}
                  </p>
                </div>
              </div>
            ))}
            {!loadingRevenueReport && !revenueReport?.topTenants.length && (
              <p className="text-sm text-slate-500">No revenue data yet.</p>
            )}
          </div>
        </div>
      </div>

      {/* Feature Adoption Heatmap */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-800">
            Feature Adoption
          </h3>
        </div>
        {loadingAdoption ? (
          <div className="h-40 flex items-center justify-center text-slate-400">
            Loading...
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
            {(featureAdoption ?? []).map((feature) => {
              const intensity = feature.adoptionRate / 100;
              const bg =
                intensity > 0.8
                  ? "bg-green-100 border-green-300"
                  : intensity > 0.5
                    ? "bg-blue-100 border-blue-300"
                    : intensity > 0.3
                      ? "bg-yellow-100 border-yellow-300"
                      : "bg-red-100 border-red-300";

              return (
                <div
                  key={feature.module}
                  className={`${bg} border rounded-lg p-3 text-center`}
                >
                  <div className="text-xs font-medium text-slate-600 truncate">
                    {feature.module}
                  </div>
                  <div className="text-lg font-bold mt-1">
                    {feature.adoptionRate.toFixed(0)}%
                  </div>
                  <div className="text-[10px] text-slate-500">
                    {feature.activeTenants}/{feature.totalEligible}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Tenant Health Scores */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-800">
            Tenant Health Scores
          </h3>
          <AlertTriangle className="h-5 w-5 text-yellow-500" />
        </div>
        {loadingHealth ? (
          <div className="h-40 flex items-center justify-center text-slate-400">
            Loading...
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-3 font-medium text-slate-600">
                    Tenant
                  </th>
                  <th className="text-center py-2 px-3 font-medium text-slate-600">
                    Health Score
                  </th>
                  <th className="text-center py-2 px-3 font-medium text-slate-600">
                    Risk Level
                  </th>
                  <th className="text-center py-2 px-3 font-medium text-slate-600">
                    Login Freq
                  </th>
                  <th className="text-center py-2 px-3 font-medium text-slate-600">
                    Feature Usage
                  </th>
                  <th className="text-center py-2 px-3 font-medium text-slate-600">
                    Billing
                  </th>
                  <th className="text-right py-2 px-3 font-medium text-slate-600">
                    Last Active
                  </th>
                </tr>
              </thead>
              <tbody>
                {(tenantHealth ?? []).map((tenant) => (
                  <tr
                    key={tenant.id}
                    className="border-b border-slate-100 hover:bg-slate-50"
                  >
                    <td className="py-2 px-3 font-medium">{tenant.name}</td>
                    <td className="py-2 px-3 text-center">
                      <HealthScoreBar score={tenant.healthScore} />
                    </td>
                    <td className="py-2 px-3 text-center">
                      <RiskBadge level={tenant.riskLevel} />
                    </td>
                    <td className="py-2 px-3 text-center">
                      {tenant.loginFrequency}/week
                    </td>
                    <td className="py-2 px-3 text-center">
                      {tenant.featureUsage}%
                    </td>
                    <td className="py-2 px-3 text-center">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          tenant.billingStatus === "active"
                            ? "bg-green-100 text-green-700"
                            : tenant.billingStatus === "overdue"
                              ? "bg-red-100 text-red-700"
                              : "bg-yellow-100 text-yellow-700"
                        }`}
                      >
                        {tenant.billingStatus}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right text-slate-500">
                      {new Date(tenant.lastActive).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function KPICard({
  title,
  value,
  subtitle,
  icon,
  bgColor,
}: {
  title: string;
  value: string;
  subtitle?: string;
  icon: ReactNode;
  bgColor: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
            {title}
          </p>
          <p className="mt-1 text-2xl font-bold text-slate-800">{value}</p>
          {subtitle && (
            <p className="mt-0.5 text-xs text-slate-400">{subtitle}</p>
          )}
        </div>
        <div className={`${bgColor} p-3 rounded-xl`}>{icon}</div>
      </div>
    </div>
  );
}

function HealthScoreBar({ score }: { score: number }) {
  const color =
    score >= 80
      ? "bg-green-500"
      : score >= 60
        ? "bg-blue-500"
        : score >= 40
          ? "bg-yellow-500"
          : "bg-red-500";

  return (
    <div className="flex items-center gap-2">
      <div className="w-16 bg-slate-100 rounded-full h-2">
        <div
          className={`h-2 rounded-full ${color}`}
          style={{ width: `${score}%` }}
        />
      </div>
      <span className="text-xs font-medium">{score}</span>
    </div>
  );
}

function RiskBadge({ level }: { level: string }) {
  const styles: Record<string, string> = {
    low: "bg-green-100 text-green-700",
    medium: "bg-yellow-100 text-yellow-700",
    high: "bg-orange-100 text-orange-700",
    critical: "bg-red-100 text-red-700",
  };

  return (
    <span
      className={`text-xs px-2 py-0.5 rounded-full font-medium ${
        styles[level] ?? "bg-slate-100 text-slate-700"
      }`}
    >
      {level}
    </span>
  );
}
